/**
 * ConversationRealtimeService — feature-level bridge giữa `DurableObjectService`
 * và các component / service của trang hội thoại.
 *
 * Trách nhiệm:
 * - Subscribe tất cả `page:event` / `business:event` / `user:event` / `mobile:transfer`
 * cần dùng trong feature hội thoại.
 * - Lọc self-broadcast: bỏ event có `senderId === currentUserId` (xem ADR-0002).
 * - Expose nhiều stream nhỏ, tách theo loại sub-event để component dễ tiêu thụ.
 * - Lọc theo `pageId` đang active (tùy chọn) qua method `forPage(pageId)`.
 *
 * Service này KHÔNG tự join room — `AuthService` đã làm sau khi đăng nhập.
 *
 */
import {inject, Injectable} from '@angular/core';
import {merge, Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {AuthService} from '@core/authentication/auth.service';
import {DurableObjectService} from '@core/realtime/durable-object.service';
import {
  BUSINESS_EVENT_TYPE,
  ConversationChangesPayload,
  DoIncomingEvent,
  DoSubEventPayload,
  MOBILE_TRANSFER_TYPE,
  MobileTransferPayload,
  PAGE_EVENT_TYPE,
  QuickMessageChangedPayload,
  RecommendProductPayload,
  ReminderPayload,
  StopTypingPayload,
  TagChangedPayload,
  TypingPayload,
  USER_EVENT_TYPE,
  ZaloPersonalTypingPayload,
  ZaloMobileSyncEventPayload,
} from '@shared/models/realtime/do-event.model';

interface MaybeSenderPayload {
  senderId?: string | number;
  userId?: string | number;
}

@Injectable({providedIn: 'root'})
export class ConversationRealtimeService {
  private readonly doService = inject(DurableObjectService);
  private readonly auth = inject(AuthService);

  // ── Page events ──────────────────────────────────────────────────────────

  readonly typing$: Observable<DoIncomingEvent<TypingPayload>> =
    this.dropSelfBroadcast(this.doService.onTyping());

  readonly stopTyping$: Observable<DoIncomingEvent<StopTypingPayload>> =
    this.dropSelfBroadcast(this.doService.onStopTyping());

  readonly conversationChanges$: Observable<DoIncomingEvent<ConversationChangesPayload>> =
    this.dropSelfBroadcast(
      this.doService.onPageEvent<ConversationChangesPayload>(
        PAGE_EVENT_TYPE.CONVERSATION_CHANGES,
      ),
    );

  /** Typing Zalo cá nhân theo contract cũ: `conversation:changes.typingEvent`. */
  readonly zaloPersonalTyping$: Observable<DoIncomingEvent<ZaloPersonalTypingPayload>> =
    this.conversationChanges$.pipe(
      filter(evt => !!evt.data?.typingEvent?.conversationId && !!evt.data.typingEvent.is_typing),
      map(evt => ({
        ...evt,
        data: evt.data.typingEvent!,
      })),
    );

  readonly zaloMobileSync$: Observable<DoIncomingEvent<ZaloMobileSyncEventPayload>> =
    this.doService.onPageEvent<ZaloMobileSyncEventPayload>('zalo:mobile-sync' as PAGE_EVENT_TYPE);

  readonly recommendProduct$: Observable<DoIncomingEvent<RecommendProductPayload>> =
    this.dropSelfBroadcast(
      this.doService.onPageEvent<RecommendProductPayload>(
        PAGE_EVENT_TYPE.CONVERSATION_RECOMMEND,
      ),
    );

  readonly tagChanged$: Observable<DoIncomingEvent<TagChangedPayload>> =
    this.dropSelfBroadcast(
      this.doService.onPageEvent<TagChangedPayload>(PAGE_EVENT_TYPE.TAG_CHANGED),
    );

  readonly quickMessageChanged$: Observable<DoIncomingEvent<QuickMessageChangedPayload>> =
    this.dropSelfBroadcast(
      this.doService.onPageEvent<QuickMessageChangedPayload>(
        PAGE_EVENT_TYPE.QUICK_MESSAGE_CHANGED,
      ),
    );

  readonly pageTracking$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onPageEvent(PAGE_EVENT_TYPE.PAGE_TRACKING),
  );

  readonly pageReconnected$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onPageEvent(PAGE_EVENT_TYPE.PAGE_RECONNECTED),
  );

  /**
   * Business Calling event (WebRTC) — FE13 `_handleBusinessCallingData()`.
   * BE bắn khi có cuộc gọi đến/đi/kết thúc.
   * KHÔNG dropSelfBroadcast vì calling event cần đến cả user khác gửi
   * (vd: đồng nghiệp bắt máy → mình cần biết để tắt chuông).
   */
  readonly calling$: Observable<DoIncomingEvent> =
    this.doService.onPageEvent(PAGE_EVENT_TYPE.CONVERSATION_CALLING);

  readonly pageTokenExpired$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onPageEvent(PAGE_EVENT_TYPE.PAGE_TOKEN_EXPIRED),
  );

  // ── Business events ──────────────────────────────────────────────────────

  readonly businessSettingsChanged$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onBusinessEvent(BUSINESS_EVENT_TYPE.SETTINGS_CHANGED),
  );

  readonly memberChanged$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onBusinessEvent(BUSINESS_EVENT_TYPE.MEMBER_CHANGED),
  );

  readonly businessTagChanged$: Observable<DoIncomingEvent<TagChangedPayload>> =
    this.dropSelfBroadcast(
      this.doService.onBusinessEvent<TagChangedPayload>(BUSINESS_EVENT_TYPE.TAG_CHANGED),
    );

  readonly businessQuickMessageChanged$:
    Observable<DoIncomingEvent<QuickMessageChangedPayload>> = this.dropSelfBroadcast(
    this.doService.onBusinessEvent<QuickMessageChangedPayload>(
      BUSINESS_EVENT_TYPE.QUICK_MESSAGE_CHANGED,
    ),
  );

  // ── User events ──────────────────────────────────────────────────────────

  readonly reminder$: Observable<DoIncomingEvent<ReminderPayload>> = this.dropSelfBroadcast(
    this.doService.onUserEvent<ReminderPayload>(USER_EVENT_TYPE.REMINDER),
  );

  readonly reminderChanges$: Observable<DoIncomingEvent<ReminderPayload>> = merge(
    this.reminder$,
    this.conversationChanges$.pipe(
      filter(evt => Array.isArray(evt.data?.reminders)),
      map(evt => ({
        ...evt,
        data: {
          type: USER_EVENT_TYPE.REMINDER,
          items: evt.data.reminders,
        } as ReminderPayload,
      })),
    ),
  );

  readonly notification$: Observable<DoIncomingEvent> = this.dropSelfBroadcast(
    this.doService.onUserEvent(USER_EVENT_TYPE.NOTIFICATION),
  );

  // ── Mobile transfer ──────────────────────────────────────────────────────
  // NOTE: mobile:transfer events ĐI VÀO user-room (user-{userId}) — chỉ user đó nhận.
  // Không dùng dropSelfBroadcast vì event này DO CHÍNH user gửi từ mobile app và
  // web browser phải xử lý nó (không phải "echo" cần lọc).

  readonly mobileTransfer$: Observable<DoIncomingEvent<MobileTransferPayload>> =
    this.doService.onMobileTransfer<MobileTransferPayload>(MOBILE_TRANSFER_TYPE.DATA);

  readonly mobilePhoneNumber$: Observable<DoIncomingEvent<MobileTransferPayload>> =
    this.doService.onMobileTransfer<MobileTransferPayload>(MOBILE_TRANSFER_TYPE.PHONE_NUMBER);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Lọc stream theo `pageId` đang active — sự kiện thuộc room khác sẽ bị bỏ.
   *
   * @example
   * realtime.forPage(activePageId, realtime.typing$).subscribe(e => ...)
   */
  forPage<T extends DoSubEventPayload>(
    pageId: string | number,
    stream: Observable<DoIncomingEvent<T>>,
  ): Observable<DoIncomingEvent<T>> {
    const expected = this.doService.buildRoomId(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      'page' as any, pageId,
    );
    return stream.pipe(filter(e => e.roomId === expected));
  }

  /** Gửi typing tới page room. */
  emitTyping(pageId: string, conversationId: string): void {
    this.doService.emitTyping(pageId, {
      conversationId,
      userId: String(this.currentUserId() ?? ''),
      userName: this.currentUserName(),
    });
  }

  /**
   * FE13 emitConversationChanges(): Gửi conversation changes tới page room
   * để các tab/coworker khác cập nhật realtime sau khi gửi tin thành công.
   */
  emitConversationChanges(pageId: string, data: Partial<ConversationChangesPayload>): void {
    if (!pageId) return;
    this.doService.sendPageEvent<ConversationChangesPayload>(pageId, {
      type: PAGE_EVENT_TYPE.CONVERSATION_CHANGES,
      ...data,
    } as ConversationChangesPayload);
  }

  /** Gửi stop-typing tới page room. */
  emitCalling(pageId: string | number, data: Record<string, unknown>): void {
    if (!pageId) return;
    this.doService.sendPageEvent(String(pageId), {
      type: PAGE_EVENT_TYPE.CONVERSATION_CALLING,
      ...data,
    });
  }

  emitStopTyping(pageId: string, conversationId: string): void {
    this.doService.emitStopTyping(pageId, {
      conversationId,
      userId: String(this.currentUserId() ?? ''),
      userName: this.currentUserName(),
    });
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private currentUserId(): string | number | undefined {
    // AuthService chưa expose getter public cho uid — đọc qua bracket để tránh lock-in.
    const a = (this.auth as unknown as { auth?: { uid?: string; displayName?: string } }).auth;
    return a?.uid;
  }

  private currentUserName(): string {
    const a = (this.auth as unknown as { auth?: { uid?: string; displayName?: string } }).auth;
    return a?.displayName ?? '';
  }

  /**
   * Bỏ event do chính user hiện tại bắn ra (tránh echo). FE-old gọi đây là
   * "filter self-broadcast" — payload phải có `senderId` hoặc `userId` để so sánh.
   */
  private dropSelfBroadcast<T extends DoSubEventPayload>(
    stream: Observable<DoIncomingEvent<T>>,
  ): Observable<DoIncomingEvent<T>> {
    return stream.pipe(
      filter(e => {
        const me = this.currentUserId();
        if (me === undefined || me === null || me === '') return true;
        const data = e.data as unknown as MaybeSenderPayload | undefined;
        const sender = data?.senderId ?? data?.userId;
        return sender === undefined || String(sender) !== String(me);
      }),
      // Đảm bảo chỉ pass-through, không thay đổi shape.
      map(e => e),
    );
  }
}
